/** Unified "输入素材 / Input Materials" renderer
 * (VISTOERA_PRODUCTION_CANVAS_TYPED_DATAFLOW_TEXT_ASSET_LINEAGE_HIGHLIGHT_AND_TOOL_REORDER_P0
 * §12–§15), shared by every Generation Node's Inspector
 * (`GenerateImageNode`/`GenerateVideoNode`/`ActionNode`'s `generate_text`)
 * so all three ALWAYS render image/text/video materials identically
 * instead of each growing its own bespoke chip markup.
 *
 * IMAGE rendering (§13 "当前图片 preload 缩略图已经做好...它应该成为统一
 * Input Materials UI 的 IMAGE renderer") is the exact same 56×56 chip +
 * hover-preview + "×" disconnect P1 already shipped — only the THUMBNAIL
 * SOURCE grew a second case (an upstream `generate_image` node's own
 * completed output, §7/§8 gen-to-gen, has no `SiteAsset` id to look up
 * via the Image Picker API, only a `generatedOutputId` servable directly
 * via `imageGenerationOutputContentUrl`).
 *
 * TEXT rendering (§14) is new: a small stacked card, 1–3 line
 * `-webkit-line-clamp` summary, "查看全文" (opens the same read-only
 * expand as clicking a chip) / "编辑源素材" (§18 — opens the shared
 * text-edit modal on the SOURCE node) / "×" (disconnect, same semantics
 * as an image chip's × — breaks the edge only, never deletes the
 * upstream Text Asset). Never the full untruncated text inline (§14
 * "不要把整篇长文本全部撑开 Generation Card"). */

import { CloseOutlined, CustomerServiceOutlined, FileTextOutlined, PictureOutlined, VideoCameraOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'
import type { DragEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { resolveCanvasImageHoverPreviewUrl, resolveCanvasImageThumbUrl } from '../canvasImageQuality'
import { computeMaterialAliases, type InputMaterial } from '../dataflow'
import ImageThumbnailHoverPreview from './ImageThumbnailHoverPreview'
import VideoInputSectionHandle from './VideoInputSectionHandle'

import { PCV_ASSET_MENTION_DND_MIME, vistoeraDndProps } from '../canvasDragContract'

/** Asset Mention drag payload MIME type (Canvas Unified Video
 * Generation Node P0 spec §13) — deliberately its own custom type
 * (never `text/plain`) so `AssetMentionPromptEditor`'s `onDrop` can
 * distinguish "an Input Material chip was dropped" from any other
 * drag source (e.g. a stray OS file/text drag) and never insert a raw
 * path/URL (spec §13 "不能变成 /asset/123.jpg"). */
export const ASSET_MENTION_DND_TYPE = PCV_ASSET_MENTION_DND_MIME

export interface AssetMentionDragPayload {
  edgeId: string
  alias: string
  kind: InputMaterial['kind']
  /** Optional preview URL for Prompt inline chip thumbnail. */
  thumbUrl?: string
}

interface Props {
  workspaceId: string
  materials: InputMaterial[]
  onDisconnect: (edgeId: string) => void
  onSelectSource: (nodeId: string) => void
  onEditText: (nodeId: string) => void
  /** Canvas Unified Video Generation Node P0 (spec §12/§13) —
   * `generate_video` ONLY; every OTHER `InputMaterialsRow` consumer
   * (`generate_image`/`generate_text`) renders identically to before
   * this round (chips stay non-draggable there, spec §39 regression
   * gate). */
  draggableMentions?: boolean
  /** VISTOERA_CONTENT_CANVAS_VIDEO_NODE_DROPZONE_PORT_MODEL_SIZE_AND_BACKGROUND_FIX_P0
   * §16 — `GenerateVideoNode` wraps this component in its OWN "参考素材"
   * Semantic Drop Zone container (which renders its own label + always
   * shows, even with zero materials, unlike this component's own
   * early-return-when-empty label) — passing `hideLabel` there avoids a
   * duplicate/conflicting "参考素材"+"输入素材" double header. Every OTHER
   * consumer omits this prop and keeps rendering its own "输入素材" label
   * exactly as before (unaffected). */
  hideLabel?: boolean
  /** VistoeraTASK-00144 (§三/§四/§五) — `generate_image` ONLY. When
   * provided, each IMAGE chip mounts its OWN real, one-to-one
   * `<Handle>` at `imageInputHandleId(material.edgeId)` (stable per
   * edge, never an array index), so an expanded Related Assets row
   * gives every connected reference image its OWN visual target
   * instead of all of them converging on the Result area above.
   * Omitted by every other consumer (`generate_video`/`generate_text`/
   * `ActionNode`) — their IMAGE chips render exactly as before. */
  imageInputHandleId?: (edgeId: string) => string
}

function useImageThumb(workspaceId: string, material: Extract<InputMaterial, { kind: 'IMAGE' }>): string | null {
  // Prefer SiteAsset thumbnail when entityId is present (try-on
  // outputAssetId, image_asset, materialized gen). Never prefer
  // image-generation content URL over a try-on SiteAsset id — that
  // produced broken chips on Generate Text.
  return resolveCanvasImageThumbUrl(workspaceId, {
    entityId: material.entityId,
    generatedOutputId: material.generatedOutputId,
    nodeType: material.nodeType,
  })
}

/** Drag Asset Into Prompt (spec §13) — a `draggable` chip stashes its
 * Asset Mention identity (`edgeId`/`alias`/`kind`, never a raw URL/path)
 * on the native drag event; `AssetMentionPromptEditor`'s `onDrop` is the
 * only consumer. A no-op (chip stays plain) whenever `draggableMentions`
 * is falsy — every other `InputMaterialsRow` consumer this round. */
export function useMentionDragProps(
  enabled: boolean | undefined,
  edgeId: string,
  alias: string | undefined,
  kind: InputMaterial['kind'],
  thumbUrl?: string | null,
) {
  if (!enabled || !alias) return {}
  return {
    ...vistoeraDndProps('asset-mention'),
    draggable: true,
    onDragStart: (e: DragEvent) => {
      const payload: AssetMentionDragPayload = {
        edgeId,
        alias,
        kind,
        ...(thumbUrl ? { thumbUrl } : {}),
      }
      e.dataTransfer.setData(ASSET_MENTION_DND_TYPE, JSON.stringify(payload))
      e.dataTransfer.effectAllowed = 'copy'
    },
  }
}

function AliasBadge({ alias }: { alias?: string }) {
  if (!alias) return null
  return <span className="pcv-mention-alias-badge">{alias}</span>
}

function materialSourceTitle(alias: string | undefined, sourceLabel: string, t: ReturnType<typeof useTranslation<'canvas'>>['t']) {
  return alias ? t('inputMaterials.sourceAliasFrom', { alias, label: sourceLabel }) : t('inputMaterials.sourceFrom', { label: sourceLabel })
}

function ImageMaterialChip({
  workspaceId,
  material,
  alias,
  draggableMentions,
  onDisconnect,
  onSelectSource,
  imageInputHandleId,
}: {
  workspaceId: string
  material: Extract<InputMaterial, { kind: 'IMAGE' }>
  alias?: string
  draggableMentions?: boolean
  onDisconnect: () => void
  onSelectSource: () => void
  imageInputHandleId?: (edgeId: string) => string
}) {
  const { t } = useTranslation('canvas')
  const thumb = useImageThumb(workspaceId, material)
  const previewUrl = resolveCanvasImageHoverPreviewUrl(workspaceId, {
    entityId: material.entityId,
    generatedOutputId: material.generatedOutputId,
    nodeType: material.nodeType,
  })
  const dragProps = useMentionDragProps(draggableMentions, material.edgeId, alias, 'IMAGE', thumb)
  const sourceTitle = materialSourceTitle(alias, material.sourceLabel, t)
  const chip = (
    <div className="pcv-gen-ref-chip nodrag" onClick={onSelectSource} title={sourceTitle} {...dragProps}>
      <AliasBadge alias={alias} />
      {thumb ? (
        <ImageThumbnailHoverPreview
          thumbUrl={thumb}
          previewUrl={previewUrl}
          alt={sourceTitle}
          className="pcv-gen-ref-chip-thumb-img"
          disabled={!material.ready}
        />
      ) : (
        <div className="pcv-gen-ref-chip-empty">{!material.ready ? <PictureOutlined /> : null}</div>
      )}
      <button
        type="button"
        className="pcv-gen-ref-chip-remove"
        title={t('inputMaterials.removeReferenceTitle')}
        aria-label={t('inputMaterials.removeReference')}
        onClick={(e) => {
          e.stopPropagation()
          onDisconnect()
        }}
      >
        <CloseOutlined />
      </button>
    </div>
  )
  const chipWithTooltip = !material.ready ? (
    <Tooltip title={t('inputMaterials.upstreamNotComplete')} color="#0e1526" mouseEnterDelay={0.15}>
      {chip}
    </Tooltip>
  ) : (
    chip
  )
  if (!imageInputHandleId) return chipWithTooltip
  return (
    // VistoeraTASK-00144 (§三/§四/§五) — this chip's OWN real,
    // one-to-one target anchor. Rendered as a SIBLING of `.pcv-gen-ref-
    // chip` (never a child) because that chip is `overflow: hidden`
    // (rounded-corner thumbnail clipping) and would silently clip a
    // handle positioned at its left edge; this outer wrapper is the
    // ONLY thing carrying `position: relative` + the flex-item box
    // model, so the drop target stays visible AND the row layout is
    // pixel-identical to before. Reuses `VideoInputSectionHandle`
    // verbatim (the same primitive `GenerateVideoNode.tsx`'s Prompt
    // section and this file's own PROMPT handle already rely on).
    <div className="pcv-gen-ref-chip-anchor-wrap">
      <VideoInputSectionHandle
        id={imageInputHandleId(material.edgeId)}
        className="pcv-handle pcv-handle--structured-asset"
        testId="image-related-asset-handle"
      />
      {chipWithTooltip}
    </div>
  )
}

function VideoMaterialChip({
  material,
  alias,
  draggableMentions,
  onDisconnect,
  onSelectSource,
}: {
  material: Extract<InputMaterial, { kind: 'VIDEO' }>
  alias?: string
  draggableMentions?: boolean
  onDisconnect: () => void
  onSelectSource: () => void
}) {
  const { t } = useTranslation('canvas')
  const dragProps = useMentionDragProps(draggableMentions, material.edgeId, alias, 'VIDEO')
  const sourceTitle = materialSourceTitle(alias, material.sourceLabel, t)
  return (
    <div className="pcv-gen-ref-chip nodrag" onClick={onSelectSource} title={sourceTitle} {...dragProps}>
      <AliasBadge alias={alias} />
      {material.ready && material.url ? (
        <video src={material.url} muted />
      ) : (
        <div className="pcv-gen-ref-chip-empty">
          <VideoCameraOutlined />
        </div>
      )}
      <button
        type="button"
        className="pcv-gen-ref-chip-remove"
        title={t('inputMaterials.removeReferenceTitle')}
        aria-label={t('inputMaterials.removeReference')}
        onClick={(e) => {
          e.stopPropagation()
          onDisconnect()
        }}
      >
        <CloseOutlined />
      </button>
    </div>
  )
}

/** Canvas Unified Video Generation Node P0 (spec §21) — a compact icon
 * chip, no waveform (spec §21 "不要为了这轮新造复杂 waveform engine"). */
function AudioMaterialChip({
  material,
  alias,
  draggableMentions,
  onDisconnect,
  onSelectSource,
}: {
  material: Extract<InputMaterial, { kind: 'AUDIO' }>
  alias?: string
  draggableMentions?: boolean
  onDisconnect: () => void
  onSelectSource: () => void
}) {
  const { t } = useTranslation('canvas')
  const dragProps = useMentionDragProps(draggableMentions, material.edgeId, alias, 'AUDIO')
  const sourceTitle = materialSourceTitle(alias, material.sourceLabel, t)
  return (
    <div className="pcv-gen-ref-chip nodrag" onClick={onSelectSource} title={sourceTitle} {...dragProps}>
      <AliasBadge alias={alias} />
      <div className="pcv-gen-ref-chip-empty">
        <CustomerServiceOutlined />
      </div>
      <button
        type="button"
        className="pcv-gen-ref-chip-remove"
        title={t('inputMaterials.removeReferenceTitle')}
        aria-label={t('inputMaterials.removeReference')}
        onClick={(e) => {
          e.stopPropagation()
          onDisconnect()
        }}
      >
        <CloseOutlined />
      </button>
    </div>
  )
}

function TextMaterialCard({
  material,
  alias,
  draggableMentions,
  onDisconnect,
  onSelectSource,
  onEditText,
}: {
  material: Extract<InputMaterial, { kind: 'TEXT' }>
  alias?: string
  draggableMentions?: boolean
  onDisconnect: () => void
  onSelectSource: () => void
  onEditText: () => void
}) {
  const { t } = useTranslation('canvas')
  const dragProps = useMentionDragProps(draggableMentions, material.edgeId, alias, 'TEXT')
  return (
    <div className="pcv-text-chip nodrag" {...dragProps}>
      <div className="pcv-text-chip-header">
        <FileTextOutlined />
        <span className="pcv-text-chip-title" title={material.sourceLabel}>
          {alias ? `${alias} · ${material.sourceLabel}` : material.sourceLabel}
        </span>
        <button type="button" className="pcv-text-chip-remove" title={t('inputMaterials.removeReference')} aria-label={t('inputMaterials.removeReference')} onClick={onDisconnect}>
          <CloseOutlined />
        </button>
      </div>
      <p className="pcv-text-chip-preview">{material.ready ? material.text || t('inputMaterials.emptyText') : t('inputMaterials.upstreamNotReady')}</p>
      <div className="pcv-text-chip-actions">
        <button type="button" className="pcv-text-chip-action" onClick={onSelectSource}>
          {t('inputMaterials.viewSource')}
        </button>
        <button type="button" className="pcv-text-chip-action" onClick={onEditText}>
          {t('inputMaterials.editSourceMaterial')}
        </button>
      </div>
    </div>
  )
}

export default function InputMaterialsRow({
  workspaceId,
  materials,
  onDisconnect,
  onSelectSource,
  onEditText,
  draggableMentions,
  hideLabel,
  imageInputHandleId,
}: Props) {
  const { t } = useTranslation('canvas')
  if (materials.length === 0) return null
  const aliases = computeMaterialAliases(materials, t)
  const imageVideoAudio = materials.filter(
    (m): m is Extract<InputMaterial, { kind: 'IMAGE' | 'VIDEO' | 'AUDIO' }> => m.kind === 'IMAGE' || m.kind === 'VIDEO' || m.kind === 'AUDIO',
  )
  const textMaterials = materials.filter((m): m is Extract<InputMaterial, { kind: 'TEXT' }> => m.kind === 'TEXT')

  return (
    <div className="pcv-input-materials nodrag">
      {hideLabel ? null : (
        <div className="pcv-input-materials-label">
          {t('inputMaterials.label')}
          {draggableMentions ? <span className="pcv-input-materials-hint">{t('inputMaterials.dragHint')}</span> : null}
        </div>
      )}
      {imageVideoAudio.length > 0 ? (
        <div className="pcv-gen-ref-row">
          {imageVideoAudio.map((m) => {
            const alias = aliases.get(m.edgeId)
            if (m.kind === 'IMAGE') {
              return (
                <ImageMaterialChip
                  key={m.edgeId}
                  workspaceId={workspaceId}
                  material={m}
                  alias={alias}
                  draggableMentions={draggableMentions}
                  onDisconnect={() => onDisconnect(m.edgeId)}
                  onSelectSource={() => onSelectSource(m.nodeId)}
                  imageInputHandleId={imageInputHandleId}
                />
              )
            }
            if (m.kind === 'VIDEO') {
              return (
                <VideoMaterialChip
                  key={m.edgeId}
                  material={m}
                  alias={alias}
                  draggableMentions={draggableMentions}
                  onDisconnect={() => onDisconnect(m.edgeId)}
                  onSelectSource={() => onSelectSource(m.nodeId)}
                />
              )
            }
            return (
              <AudioMaterialChip
                key={m.edgeId}
                material={m}
                alias={alias}
                draggableMentions={draggableMentions}
                onDisconnect={() => onDisconnect(m.edgeId)}
                onSelectSource={() => onSelectSource(m.nodeId)}
              />
            )
          })}
        </div>
      ) : null}
      {textMaterials.length > 0 ? (
        <div className="pcv-text-chip-stack">
          {textMaterials.map((m) => (
            <TextMaterialCard
              key={m.edgeId}
              material={m}
              alias={aliases.get(m.edgeId)}
              draggableMentions={draggableMentions}
              onDisconnect={() => onDisconnect(m.edgeId)}
              onSelectSource={() => onSelectSource(m.nodeId)}
              onEditText={() => onEditText(m.nodeId)}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}
